import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Award, ShieldCheck, Users, ChevronRight, Star, ExternalLink, Handshake, Search, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

// SBS 5.0 Logo URL (Minimalist Gold on Black)
const LOGO_URL = "https://customer-assets.emergentagent.com/job_skillbharat-samman/artifacts/i50nrqf7_WhatsApp%20Image%202026-01-10%20at%208.04.27%20AM.jpeg";

// Grand Queen Chess Trophy Background
const QUEEN_TROPHY_URL = "https://static.prod-images.emergentagent.com/jobs/ac77e725-ab87-494e-a252-e13976307c85/images/3afb1d71eb74369c68e93a2b48df79a0844948bf316f3f7b298c5c1729400d3a.png";

// Organization Logos (Transparent)
const EPC_LOGO = "https://customer-assets.emergentagent.com/job_skillbharatsmn/artifacts/brol5ayy_Gemini_Generated_Image_bq6i1tbq6i1tbq6i.png";
const EPC_ICON = "https://customer-assets.emergentagent.com/job_skillbharatsmn/artifacts/8kcj0azx_Gemini_Generated_Image_27nlin27nlin27nl.png";
const SBA_LOGO = "https://customer-assets.emergentagent.com/job_skillbharatsmn/artifacts/ownsd6e2_Gemini_Generated_Image_ajv85vajv85vajv8.png";

// UNESCO Collaboration Logos (Transparent)
const UNESCO_LOGO_1 = "https://customer-assets.emergentagent.com/job_skillbharatsmn/artifacts/wlcdx9ro_Gemini_Generated_Image_q3kqfjq3kqfjq3kq.png";
const UNESCO_LOGO_2 = "https://customer-assets.emergentagent.com/job_skillbharatsmn/artifacts/7fa0a01h_Gemini_Generated_Image_3vmsr53vmsr53vms.png";
const WORLD_GENESIS_LOGO = "https://customer-assets.emergentagent.com/job_skillbharatsmn/artifacts/2z9lt8ws_image.png";
const COLLAB_LOGO_3 = "https://customer-assets.emergentagent.com/job_skillbharatsmn/artifacts/t7f9y3qp_Gemini_Generated_Image_1zlbfm1zlbfm1zlb.png";
const COLLAB_LOGO_4 = "https://customer-assets.emergentagent.com/job_skillbharatsmn/artifacts/bmu7s90s_Gemini_Generated_Image_q0w3t2q0w3t2q0w3.png";

const LandingPage = () => {
  const navigate = useNavigate();
  const [checkingCampaign, setCheckingCampaign] = useState(true);

  useEffect(() => {
    // Check for active landing page campaign
    const checkActiveCampaign = async () => {
      try {
        const response = await fetch(`${API}/api/landing/active`);
        if (response.ok) {
          const data = await response.json();
          if (data.has_campaign && data.campaign) {
            const { landing_type, redirect_path } = data.campaign;
            
            // Handle different landing types
            if (landing_type === 'redirect' && redirect_path) {
              navigate(redirect_path, { replace: true });
              return;
            } else if (landing_type === 'school_parivar') {
              navigate('/school-parivar-awards', { replace: true });
              return;
            }
            // 'sba_default' and 'sbs_event' show this page
          }
        }
      } catch (error) {
        console.error('Error checking campaign:', error);
      } finally {
        setCheckingCampaign(false);
      }
    };

    checkActiveCampaign();
  }, [navigate]);

  // Show loading while checking campaign
  if (checkingCampaign) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] relative overflow-x-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iI2Q0YWYzNyIgc3Ryb2tlLXdpZHRoPSIwLjUiIG9wYWNpdHk9IjAuMDUiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')]"></div>
      
      {/* Golden Gradient Accent */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#d4af37]/5 to-transparent pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-[#0a0a0a] to-transparent pointer-events-none"></div>
      
      {/* Grand Queen Trophy - Desktop only as background, Mobile: inline */}
      <div className="hidden md:flex absolute inset-0 items-center justify-end pointer-events-none overflow-hidden">
        <img 
          src={QUEEN_TROPHY_URL} 
          alt="Skill Bharat Samman 5 Trophy" 
          className="
            md:w-[380px] md:opacity-40 md:mr-4
            lg:w-[480px] lg:opacity-45 lg:mr-12
            xl:w-[580px] xl:mr-20
            2xl:w-[680px] 2xl:mr-28
            object-contain drop-shadow-[0_0_80px_rgba(212,175,55,0.25)]
            transition-all duration-300
          "
        />
      </div>
      
      {/* Main Content */}
      <div className="relative z-10 flex flex-col p-4 sm:p-6 md:p-16 lg:p-24 pb-24 md:pb-16 md:min-h-screen md:justify-center">
        <div className="max-w-3xl">
          {/* Logo */}
          <div className="mb-6">
            <img 
              src={LOGO_URL} 
              alt="Skill Bharat Samman 5.0" 
              className="h-32 md:h-40 lg:h-48 drop-shadow-2xl"
            />
          </div>
          
          {/* Tagline */}
          <p className="text-white/60 text-lg md:text-xl mb-4 max-w-lg leading-relaxed">
            Celebrating the excellence in education
          </p>
          
          {/* Event Details */}
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm">
            <div className="flex items-center gap-2 text-[#d4af37]">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="font-semibold">31st January 2026</span>
            </div>
            <span className="hidden sm:inline text-white/30">|</span>
            <div className="flex items-center gap-2 text-white/60">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>9:30 AM - 5:00 PM</span>
            </div>
            <span className="hidden sm:inline text-white/30">|</span>
            <div className="flex items-center gap-2 text-white/60">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Jio World Convention Centre, BKC, Mumbai</span>
            </div>
          </div>
          
          {/* UNESCO Collaboration - Below Title */}
          <div className="mb-10 p-4 bg-[#1a1a1a]/60 backdrop-blur-sm border border-white/10 rounded-xl inline-block">
            <div className="flex items-center gap-2 mb-3">
              <Handshake className="w-4 h-4 text-[#d4af37]" />
              <span className="text-white/60 text-xs font-medium uppercase tracking-wider">In Collaboration With</span>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <img src={UNESCO_LOGO_1} alt="UNESCO Centre for Peace" className="h-10 md:h-12 object-contain" />
              <img src={UNESCO_LOGO_2} alt="US Federation of UNESCO Clubs" className="h-10 md:h-12 object-contain" />
              <img src={WORLD_GENESIS_LOGO} alt="World Genesis Foundation" className="h-10 md:h-12 object-contain" />
            </div>
          </div>
          
          {/* Lane Selection Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-2xl">
            {/* Elite Principals Club Members Entry */}
            <Card 
              data-testid="epc-lane-card"
              className="cursor-pointer bg-[#1a1a1a]/80 backdrop-blur-sm border-[#d4af37]/40 hover:border-[#d4af37] transition-all duration-300 hover:shadow-[0_0_30px_rgba(212,175,55,0.2)]"
              onClick={() => navigate('/nominate/vip')}
            >
              <CardContent className="p-5 md:p-8">
                <div className="mb-3 md:mb-4">
                  <img src={EPC_ICON} alt="EPC" className="w-12 h-12 md:w-16 md:h-16 object-contain" />
                </div>
                <h3 className="text-lg md:text-xl font-bold mb-1 md:mb-2 text-white">Elite Principals Club</h3>
                <p className="text-[#d4af37] text-xs md:text-sm font-semibold mb-2 md:mb-3">Members Entry</p>
                <p className="text-xs md:text-sm text-white/50 mb-3 md:mb-4">
                  Fast-track verification for EPC members.
                </p>
                <div className="flex items-center text-[#d4af37] text-xs md:text-sm font-semibold">
                  Enter <ChevronRight className="w-4 h-4 ml-1" />
                </div>
              </CardContent>
            </Card>
            
            {/* Public Lane */}
            <Card 
              data-testid="public-lane-card"
              className="cursor-pointer bg-[#1a1a1a]/80 backdrop-blur-sm border-white/10 hover:border-blue-400/50 transition-all duration-300"
              onClick={() => navigate('/nominate/public')}
            >
              <CardContent className="p-5 md:p-8">
                <div className="mb-3 md:mb-4">
                  <div className="p-2 md:p-3 bg-blue-500/20 rounded-lg w-fit">
                    <Users className="w-6 h-6 md:w-7 md:h-7 text-blue-400" />
                  </div>
                </div>
                <h3 className="text-lg md:text-xl font-bold mb-1 md:mb-2 text-white">Open Nomination</h3>
                <p className="text-blue-400 text-xs md:text-sm font-semibold mb-2 md:mb-3">Open Entry</p>
                <p className="text-xs md:text-sm text-white/50 mb-3 md:mb-4">
                  Education Leaders nominate yourself
                </p>
                <div className="flex items-center text-blue-400 text-xs md:text-sm font-semibold">
                  Nominate <ChevronRight className="w-4 h-4 ml-1" />
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Mobile Trophy - Shows on mobile only */}
          <div className="flex md:hidden justify-center mt-6 mb-4">
            <img 
              src={QUEEN_TROPHY_URL} 
              alt="Skill Bharat Samman 5 Trophy" 
              className="w-[200px] h-auto opacity-60 object-contain drop-shadow-[0_0_40px_rgba(212,175,55,0.3)]"
            />
          </div>
          
          {/* Gallery Link */}
          <div className="mt-6 md:mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 flex-wrap">
            <Button
              data-testid="check-status-button"
              variant="ghost"
              className="text-white/40 hover:text-[#d4af37] transition-colors text-sm"
              onClick={() => navigate('/track')}
            >
              <Search className="w-4 h-4 mr-2" />
              Check Application Status
            </Button>
            <span className="hidden sm:inline text-white/20">|</span>
            <Button
              variant="ghost"
              className="text-white/40 hover:text-[#d4af37] transition-colors text-sm"
              onClick={() => navigate('/gallery')}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              View Hall of Excellence
            </Button>
            <span className="hidden sm:inline text-white/20">|</span>
            <Button
              variant="ghost"
              className="text-white/40 hover:text-[#d4af37] transition-colors text-sm"
              onClick={() => navigate('/sba')}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              About Skill Bharat Association
            </Button>
          </div>
        </div>
      </div>
      
      {/* Admin Button - positioned higher to avoid platform badge */}
      <Button
        data-testid="admin-button"
        variant="ghost"
        size="icon"
        className="fixed bottom-20 md:bottom-24 right-4 md:right-8 p-3 md:p-4 bg-[#1a1a1a]/80 backdrop-blur-sm border border-white/10 hover:border-[#d4af37] hover:text-[#d4af37] rounded-lg z-[9999] transition-all"
        onClick={() => navigate('/admin')}
      >
        <ShieldCheck className="w-5 h-5 md:w-6 md:h-6" />
      </Button>
      
      {/* Footer */}
      <div className="relative z-10 px-4 py-4 md:absolute md:bottom-8 md:left-16 lg:left-24 md:right-16 lg:right-24 flex flex-col sm:flex-row items-center sm:items-center justify-between gap-2 border-t border-white/10 md:border-0">
        <span className="text-xs text-white/30 text-center sm:text-left">
          © 2023-2026 Skill Bharat Association. All Rights Reserved.
        </span>
        <div className="flex items-center gap-4">
          <a 
            href="https://www.skillba.org" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs text-white/30 hover:text-[#d4af37] transition-colors"
          >
            www.skillba.org
          </a>
          <span className="text-white/20">|</span>
          <a 
            href="https://shiftora.ai" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs text-white/30 hover:text-[#d4af37] transition-colors"
          >
            built by shiftora.ai
          </a>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
